/**
 * Which screens a person may open, by their role in the organisation. The
 * sidebar and the tab bar draw from this, and a screen it says no to is
 * "Nothing here" even when its address is typed: nothing answers that the app
 * would not take you to (decisions.md). The worker refuses the same reads in
 * its own words, from the same rules in `@utuwrk/core`.
 *
 * The plan is the other courtesy: a screen the organisation's plan does not
 * have is not offered, and the worker refuses it in words if it is typed.
 *
 * An employee's page is the one the app cannot decide alone: their manager may
 * open it whatever their role, and only the worker knows who that is.
 */

import {
  canExportData,
  featureProblem,
  planOf,
  canOversee,
  canReadAudit,
  canStaff,
  canStart,
  type MembershipRole,
} from "@utuwrk/core";
import type { Screen } from "./router.js";

export function mayOpen(
  screen: Screen,
  role: MembershipRole | null,
  isAdmin: boolean,
  plan: string | null = null,
): boolean {
  switch (screen.name) {
    case "preview":
    case "missing":
    // Making an organisation of your own is anybody's; the worker says whether signup is open.
    case "signup":
      return true;
    case "admin":
      return isAdmin;
    case "queue":
    case "notices":
    case "people":
    case "card":
    case "employee":
      return role !== null;
    case "onboardings":
    case "offboardings":
    case "board":
    case "summary":
    case "week":
    case "employees":
      return role !== null && canOversee({ role });
    case "overdue":
      return role !== null && canOversee({ role }) && featureProblem(planOf(plan), "dashboards") === null;
    case "audit":
    case "log":
      return role !== null && canReadAudit({ role, id: "", department: "" });
    case "export":
      return role !== null && canExportData({ role });
    case "settings":
      return role !== null && canStaff({ role, id: "", department: "" });
    case "setup":
      return role === "admin";
    case "start":
    case "leave":
    // Templates are read by whoever starts work, and changed by an admin (the worker refuses the rest).
    case "templates":
    case "template":
      return role !== null && canStart({ role, id: "", department: "" });
  }
}
